import React, { useCallback, useMemo } from 'react';

import { Box, Button, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import ExpressionPreview from '../ExpressionPreview';
import {
  GENERATE_CANDIDATE_INDEX_LABEL,
  GENERATE_CANDIDATE_SELECT_BUTTON_LABEL,
  GENERATE_PREVIEW_UNAVAILABLE_HELPER,
} from '../GenerateExpression.constants';
import {
  candidateActionsRowStyles,
  candidateCardHeaderStyles,
  candidateCardStyles,
  candidateIndexBadgeStyles,
} from '../GenerateExpression.styles';
import { GenerateExpressionTestIds } from '../GenerateExpression.testIds';
import { parseProposedAst } from '../previewClauses.utils';

const CandidateCard = ({
  candidate,
  index,
  isNewExpressionsParsingLogicEnabled,
  onSelect,
  schemaDomain,
  schemaJson = null,
}) => {
  const onSelectClick = useCallback(() => onSelect(candidate, index), [candidate, index, onSelect]);

  const proposedAst = useMemo(
    () => parseProposedAst(candidate?.expressionJson),
    [candidate?.expressionJson],
  );

  return (
    <Box data-testid={GenerateExpressionTestIds.MODAL_CANDIDATE_CARD} sx={candidateCardStyles}>
      <Box sx={candidateCardHeaderStyles}>
        <Typography sx={candidateIndexBadgeStyles} variant="bold12">
          {`${GENERATE_CANDIDATE_INDEX_LABEL} ${index + 1}`}
        </Typography>
        {!_.isEmpty(_.trim(candidate?.summary)) && (
          <Typography color="text.primary" variant="regular14">
            {candidate?.summary}
          </Typography>
        )}
      </Box>

      <ExpressionPreview
        isExplainDisabled
        emptyHelperText={GENERATE_PREVIEW_UNAVAILABLE_HELPER}
        isNewExpressionsParsingLogicEnabled={isNewExpressionsParsingLogicEnabled}
        proposedAst={proposedAst}
        schemaDomain={schemaDomain}
        schemaJson={schemaJson}
      />

      <Box sx={candidateActionsRowStyles}>
        <Button
          data-testid={GenerateExpressionTestIds.MODAL_CANDIDATE_SELECT}
          variant="contained"
          onClick={onSelectClick}
        >
          {GENERATE_CANDIDATE_SELECT_BUTTON_LABEL}
        </Button>
      </Box>
    </Box>
  );
};

CandidateCard.propTypes = {
  candidate: PropTypes.shape({
    expressionJson: PropTypes.string,
    summary: PropTypes.string,
    assumptions: PropTypes.arrayOf(PropTypes.string),
    aiUsageMetricsId: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  isNewExpressionsParsingLogicEnabled: PropTypes.bool.isRequired,
  schemaDomain: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  schemaJson: PropTypes.string,
};

export default CandidateCard;
